import { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Card,
  DataTable,
  Text,
  Banner,
  Spinner,
  Button,
  InlineStack,
  BlockStack,
  Badge
} from '@shopify/polaris';

const DEFAULT_SETTINGS = {
  discount_margin_buffer_percent: 10,
  shipping_cost_alert_percent: 12
};

function toNumber(value, fallback = 0) {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export default function Orders({ shop, shopData, showToast }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [stats, setStats] = useState({
    total_revenue: 0,
    total_cogs: 0,
    gross_profit: 0,
    total_profit: 0,
    avg_margin: 0,
    negative_margin_count: 0,
    total_discounts: 0,
    total_shipping: 0,
    total_fees: 0
  });

  const LIMIT = 50;
  const settings = {
    ...DEFAULT_SETTINGS,
    ...(shopData?.settings || {})
  };

  const fetchOrders = useCallback(async (newOffset = 0) => {
    if (!shop) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/orders?shop=${shop}&limit=${LIMIT}&offset=${newOffset}`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || 'Failed to fetch orders';
        throw new Error(errorMessage);
      }

      const data = await response.json();

      if (!Array.isArray(data)) {
        console.error('Invalid orders response:', data);
        setOrders([]);
        setHasMore(false);
        return;
      }

      setOrders(data);
      setHasMore(data.length === LIMIT);
      setOffset(newOffset);

      const revenue = data.reduce((sum, order) => sum + toNumber(order.total_price), 0);
      const cogs = data.reduce((sum, order) => sum + toNumber(order.total_cogs), 0);
      const grossProfit = data.reduce((sum, order) => sum + toNumber(order.gross_profit), 0);
      const profit = data.reduce((sum, order) => sum + toNumber(order.net_profit), 0);
      const avgMargin = data.length > 0
        ? data.reduce((sum, order) => sum + toNumber(order.margin_percent), 0) / data.length
        : 0;
      const negativeCount = data.filter((order) => toNumber(order.margin_percent) < 0).length;
      const discounts = data.reduce((sum, order) => sum + toNumber(order.total_discounts), 0);
      const shipping = data.reduce((sum, order) => sum + toNumber(order.shipping_cost), 0);
      const fees = data.reduce((sum, order) => sum + toNumber(order.transaction_fees), 0);

      setStats({
        total_revenue: revenue,
        total_cogs: cogs,
        gross_profit: grossProfit,
        total_profit: profit,
        avg_margin: avgMargin,
        negative_margin_count: negativeCount,
        total_discounts: discounts,
        total_shipping: shipping,
        total_fees: fees
      });
    } catch (error) {
      console.error('Error fetching orders:', error);
      showToast(error.message || 'Failed to load orders', true);
      setOrders([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [shop, showToast]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleNextPage = () => {
    fetchOrders(offset + LIMIT);
  };

  const handlePrevPage = () => {
    fetchOrders(Math.max(0, offset - LIMIT));
  };

  const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(toNumber(value));

  const formatPercent = (value) => `${toNumber(value).toFixed(1)}%`;

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });

  const getMarginTone = (margin) => {
    if (margin < 0) return 'critical';
    if (margin < 15) return 'warning';
    if (margin < 30) return 'info';
    return 'success';
  };

  const buildLeakSignals = (order) => {
    const margin = toNumber(order.margin_percent);
    const revenue = toNumber(order.total_price);
    const discountRate = revenue > 0 ? (toNumber(order.total_discounts) / revenue) * 100 : 0;
    const shippingRate = revenue > 0 ? (toNumber(order.shipping_cost) / revenue) * 100 : 0;
    const signals = [];

    if (margin < 0) {
      signals.push(<Badge key="negative" tone="critical">Negative margin</Badge>);
    }
    if (discountRate > settings.discount_margin_buffer_percent) {
      signals.push(<Badge key="discount" tone="warning">Discount drag</Badge>);
    }
    if (shippingRate > settings.shipping_cost_alert_percent) {
      signals.push(<Badge key="shipping" tone="warning">High shipping</Badge>);
    }

    if (signals.length === 0) {
      signals.push(<Badge key="healthy" tone="success">Healthy</Badge>);
    }

    return <InlineStack gap="100" wrap>{signals}</InlineStack>;
  };

  const negativeMarginCount = stats.negative_margin_count;
  const discountBreachCount = orders.filter((order) => {
    const revenue = toNumber(order.total_price);
    if (revenue <= 0) return false;
    return ((toNumber(order.total_discounts) / revenue) * 100) > settings.discount_margin_buffer_percent;
  }).length;
  const highShippingCount = orders.filter((order) => {
    const revenue = toNumber(order.total_price);
    if (revenue <= 0) return false;
    return ((toNumber(order.shipping_cost) / revenue) * 100) > settings.shipping_cost_alert_percent;
  }).length;

  const rows = orders.map((order) => [
    order.order_number || '-',
    order.order_date ? formatDate(order.order_date) : '-',
    formatCurrency(order.total_price),
    formatCurrency(order.total_discounts),
    formatCurrency(order.total_cogs),
    formatCurrency(order.shipping_cost),
    formatCurrency(order.transaction_fees),
    formatCurrency(order.net_profit),
    <Badge tone={getMarginTone(toNumber(order.margin_percent))}>
      {formatPercent(order.margin_percent)}
    </Badge>,
    buildLeakSignals(order)
  ]);

  return (
    <Page
      title="Orders"
      subtitle="True profit per order after COGS, shipping, fees, and discounts"
    >
      <BlockStack gap="400">
        <Card>
          <BlockStack gap="300">
            <Text variant="headingMd" as="h2">Order-level profit summary</Text>
            <InlineStack gap="600" wrap>
              <BlockStack gap="100">
                <Text variant="bodySm" tone="subdued">Revenue</Text>
                <Text variant="headingLg" as="p">{formatCurrency(stats.total_revenue)}</Text>
              </BlockStack>
              <BlockStack gap="100">
                <Text variant="bodySm" tone="subdued">Gross Profit</Text>
                <Text variant="headingLg" as="p" tone="success">{formatCurrency(stats.gross_profit)}</Text>
              </BlockStack>
              <BlockStack gap="100">
                <Text variant="bodySm" tone="subdued">Net Profit</Text>
                <Text variant="headingLg" as="p" tone={stats.total_profit >= 0 ? 'success' : 'critical'}>
                  {formatCurrency(stats.total_profit)}
                </Text>
              </BlockStack>
              <BlockStack gap="100">
                <Text variant="bodySm" tone="subdued">Average Margin</Text>
                <Text variant="headingLg" as="p">{formatPercent(stats.avg_margin)}</Text>
              </BlockStack>
              <BlockStack gap="100">
                <Text variant="bodySm" tone="subdued">Discount Leakage</Text>
                <Text variant="headingLg" as="p" tone={discountBreachCount > 0 ? 'warning' : undefined}>
                  {formatCurrency(stats.total_discounts)}
                </Text>
              </BlockStack>
              <BlockStack gap="100">
                <Text variant="bodySm" tone="subdued">Shipping Drag</Text>
                <Text variant="headingLg" as="p" tone={highShippingCount > 0 ? 'warning' : undefined}>
                  {formatCurrency(stats.total_shipping)}
                </Text>
              </BlockStack>
            </InlineStack>
          </BlockStack>
        </Card>

        {(negativeMarginCount > 0 || discountBreachCount > 0 || highShippingCount > 0) && (
          <Banner
            title="Profit leaks detected on this page"
            tone="warning"
          >
            <p>
              {negativeMarginCount > 0 ? `${negativeMarginCount} order(s) are negative-margin. ` : ''}
              {discountBreachCount > 0 ? `${discountBreachCount} order(s) exceeded your discount buffer. ` : ''}
              {highShippingCount > 0 ? `${highShippingCount} order(s) crossed your shipping-cost rule.` : ''}
            </p>
          </Banner>
        )}

        <Card>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <Spinner size="large" />
            </div>
          ) : orders.length === 0 ? (
            <Banner>
              <p>No orders found. Sync your store to start reviewing order-level profit leaks.</p>
            </Banner>
          ) : (
            <BlockStack gap="400">
              <DataTable
                columnContentTypes={[
                  'text',
                  'text',
                  'numeric',
                  'numeric',
                  'numeric',
                  'numeric',
                  'numeric',
                  'numeric',
                  'text',
                  'text'
                ]}
                headings={[
                  'Order #',
                  'Date',
                  'Revenue',
                  'Discounts',
                  'COGS',
                  'Shipping',
                  'Fees',
                  'Net Profit',
                  'Margin %',
                  'Leak Signals'
                ]}
                rows={rows}
                hoverable
              />

              <InlineStack align="center" gap="200">
                <Button onClick={handlePrevPage} disabled={offset === 0}>
                  Previous
                </Button>
                <Text variant="bodySm" as="span" tone="subdued">
                  Showing {offset + 1} - {offset + orders.length}
                </Text>
                <Button onClick={handleNextPage} disabled={!hasMore}>
                  Next
                </Button>
              </InlineStack>
            </BlockStack>
          )}
        </Card>
      </BlockStack>
    </Page>
  );
}
